/**
 * Keeps the Dock icon and the bell saying the same thing.
 *
 * This lives beside the bell rather than beside the signal stream on purpose.
 * The bell shows `unseenSignals(...)`, which is the live counters minus what
 * this browser has already cleared, and that subtraction is the difference
 * between a number people trust and a number they learn to ignore. Reading the
 * raw stream instead would put a five on the Dock over a bell showing nothing.
 *
 * Everything here is inert in a browser: `setBadge` and `notify` no-op when the
 * page is not inside the macOS app.
 */
import { useEffect, useRef } from "react";
import { countsOf, notify, raisedSince, setBadge, type SignalCounts } from "../../lib/desktop";
import type { SignalRow } from "./signals";

export function useDesktopBadge(rows: SignalRow[], label: (row: SignalRow) => string) {
  /**
   * What the counts were last time we looked.
   *
   * Null until the first look, which is what stops the app from announcing
   * yesterday's whole backlog the moment it starts.
   */
  const seen = useRef<SignalCounts | null>(null);
  /** Kept in a ref so a re-render with a new closure does not re-announce. */
  const labelRef = useRef(label);
  labelRef.current = label;

  useEffect(() => {
    setBadge(rows.length);
    for (const row of raisedSince(rows, seen.current)) {
      notify("WebAdmin CRM", labelRef.current(row));
    }
    seen.current = countsOf(rows);
  }, [rows]);
}
